import clsx from 'clsx';
import { Loader2, X } from 'lucide-react';
import { useEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

// ───────────── buttons ─────────────

type Variant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 disabled:bg-brand-600/50',
  secondary: 'bg-white text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50 disabled:text-slate-400',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 disabled:bg-rose-600/50',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-600/50',
  ghost: 'text-brand-700 hover:bg-brand-50 disabled:text-slate-400',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg'; loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-colors disabled:cursor-not-allowed',
        size === 'sm' && 'min-h-9 px-3 text-sm',
        size === 'md' && 'min-h-11 px-4 text-base',
        size === 'lg' && 'min-h-14 px-6 text-lg',
        VARIANT[variant],
        className,
      )}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

// ───────────── layout ─────────────

export function Card({ className, children, ...rest }: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={clsx('rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200', className)}>
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="truncate text-2xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-600">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Empty({ title, hint, icon, action }: { title: string; hint?: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-10 text-center">
      {icon && <div className="mb-3 text-slate-400">{icon}</div>}
      <div className="font-semibold text-slate-800">{title}</div>
      {hint && <div className="mt-1 max-w-xs text-sm text-slate-600">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export const Spinner = ({ label = 'Loading' }: { label?: string }) => (
  <div className="flex items-center justify-center gap-2 py-10 text-slate-500" role="status">
    <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
    <span className="text-sm">{label}…</span>
  </div>
);

export function ErrorNote({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <div role="alert" className="rounded-xl bg-rose-50 p-4 text-sm text-rose-800 ring-1 ring-rose-200">
      {error instanceof Error ? error.message : 'Something went wrong'}
      {retry && (
        <button onClick={retry} className="ml-2 font-semibold underline">
          Try again
        </button>
      )}
    </div>
  );
}

// ───────────── badges ─────────────

const TONES = {
  gray: 'bg-slate-100 text-slate-700',
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-emerald-100 text-emerald-800',
  amber: 'bg-amber-100 text-amber-900',
  red: 'bg-rose-100 text-rose-800',
  purple: 'bg-violet-100 text-violet-800',
} as const;
export type Tone = keyof typeof TONES;

export const Badge = ({ tone = 'gray', children, className }: { tone?: Tone; children: ReactNode; className?: string }) => (
  <span className={clsx('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold', TONES[tone], className)}>{children}</span>
);

export const VISIT_TONE: Record<string, [string, Tone]> = {
  pending: ['Waiting for resident', 'amber'],
  approved: ['Approved', 'green'],
  rejected: ['Rejected', 'red'],
  no_response: ['No response', 'amber'],
  entered: ['Inside', 'blue'],
  exited: ['Left', 'gray'],
  cancelled: ['Cancelled', 'gray'],
  expired: ['Expired', 'gray'],
};
export const TICKET_TONE: Record<string, [string, Tone]> = {
  open: ['Open', 'blue'],
  acknowledged: ['Acknowledged', 'purple'],
  in_progress: ['In progress', 'amber'],
  resolved: ['Resolved', 'green'],
  closed: ['Closed', 'gray'],
  reopened: ['Reopened', 'red'],
};
export const StatusBadge = ({ map, status }: { map: Record<string, [string, Tone]>; status: string }) => {
  const [label, tone] = map[status] ?? [status, 'gray' as Tone];
  return <Badge tone={tone}>{label}</Badge>;
};

// ───────────── forms ─────────────

const fieldCls =
  'w-full rounded-xl border-0 bg-white px-3.5 py-3 text-base text-slate-900 shadow-sm ring-1 ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-brand-500 disabled:bg-slate-100';

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-800">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
      {error && <span className="mt-1 block text-xs font-medium text-rose-700">{error}</span>}
    </label>
  );
}
export const Input = ({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) => <input {...p} className={clsx(fieldCls, className)} />;
export const Textarea = ({ className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea rows={4} {...p} className={clsx(fieldCls, className)} />;
export const Select = ({ className, children, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...p} className={clsx(fieldCls, 'appearance-none bg-[length:1.25rem] pr-9', className)}>
    {children}
  </select>
);

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<{ value: T; label: string; badge?: number }> }) {
  return (
    <div role="tablist" className="flex gap-1 rounded-xl bg-slate-200/70 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={clsx(
            'min-h-10 flex-1 rounded-lg px-3 text-sm font-semibold transition-colors',
            value === o.value ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-600 hover:text-slate-900',
          )}
        >
          {o.label}
          {!!o.badge && <span className="ml-1.5 rounded-full bg-rose-600 px-1.5 py-0.5 text-[11px] text-white">{o.badge}</span>}
        </button>
      ))}
    </div>
  );
}

// ───────────── bottom sheet ─────────────

export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Move focus into the dialog. Otherwise the button that opened it keeps focus behind the popup, and
    // pressing Enter or Space would silently press it again (e.g. issue a second login code).
    if (open) panel.current?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label={title}>
      <button aria-label="Close" className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div ref={panel} tabIndex={-1} className="relative max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-slate-50 p-5 shadow-xl outline-none sm:rounded-3xl" style={{ paddingBottom: 'calc(var(--safe-bottom) + 1.25rem)' }}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="rounded-full p-2 text-slate-500 hover:bg-slate-200">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ───────────── helpers ─────────────

export function timeAgo(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}
export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
export const KIND_LABEL: Record<string, string> = { guest: 'Guest', delivery: 'Delivery', cab: 'Cab', service: 'Service', other: 'Other' };
